import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  TextField,
  Typography,
} from "@material-ui/core";
import { hostname } from "../providers/api";
import { opportunityPricingGroups } from "./commercialPricingState";

const money = (minor, currency = "EUR") =>
  new window.Intl.NumberFormat("en-IE", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(Number(minor || 0) / 100);
const apiError = async (response) => {
  const body = await response.text();
  try {
    return JSON.parse(body).message || body;
  } catch (_error) {
    return body || `Request failed (${response.status})`;
  }
};

export default function CommercialPriceCalculator({
  token,
  opportunity,
  onClose,
  onSaved,
}) {
  const [catalog, setCatalog] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [items, setItems] = useState({});
  const [contractYears, setContractYears] = useState(1);
  const [annualEscalationPercent, setAnnualEscalationPercent] = useState(0);
  const [discountPercent, setDiscountPercent] = useState(0);
  const [setupDiscountPercent, setSetupDiscountPercent] = useState(0);
  const [fixedDiscount, setFixedDiscount] = useState(0);
  const [specialTerms, setSpecialTerms] = useState("");
  const [preview, setPreview] = useState(null);
  const headers = useMemo(
    () => ({
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    }),
    [token],
  );
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    Promise.all([
      fetch(`${hostname}/commercial/opportunities/${opportunity.id}/pricing-catalog`, { headers }).then(
        async (r) => {
          if (!r.ok)
            throw new Error(
              r.status === 404
                ? "No published pricing catalog is available. Configure and publish one in Delivery > Pricing."
                : await apiError(r),
            );
          return r.json();
        },
      ),
      fetch(`${hostname}/commercial/opportunities/${opportunity.id}/quotes`, {
        headers,
      }).then((r) => (r.ok ? r.json() : [])),
    ])
      .then(([nextCatalog, nextQuotes]) => {
        if (!cancelled) {
          setCatalog(nextCatalog);
          setQuotes(nextQuotes);
        }
      })
      .catch((cause) => {
        if (!cancelled) setError(cause.message);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [headers, opportunity.id]);
  const body = () => ({
    catalogId: catalog.id,
    items: Object.entries(items)
      .filter(([, value]) => value.selected)
      .map(([skuCode, value]) => {
        const item = {
          skuCode,
          quantity: Number(value.quantity || 1),
        };
        if (value.customPricing) {
          item.customAmountMinor = Math.round(
            Number(value.customAmount || 0) * 100,
          );
          item.customPricingReason = String(
            value.customPricingReason || "",
          ).trim();
        }
        return item;
      }),
    contractYears: Number(contractYears),
    annualEscalationPercent: Number(annualEscalationPercent),
    discountPercent: Number(discountPercent),
    setupDiscountPercent: Number(setupDiscountPercent),
    fixedDiscountMinor: Math.round(Number(fixedDiscount || 0) * 100),
    specialTerms,
  });
  const request = async (finalize = false) => {
    setSaving(true);
    setError(null);
    try {
      const response = await fetch(
        `${hostname}/commercial/opportunities/${opportunity.id}/${finalize ? "quotes" : "price-preview"}`,
        { method: "POST", headers, body: JSON.stringify(body()) },
      );
      if (!response.ok) throw new Error(await apiError(response));
      const result = await response.json();
      setPreview(result);
      if (finalize) onSaved(result);
    } catch (cause) {
      setError(cause.message);
    } finally {
      setSaving(false);
    }
  };
  const groupedSkus = opportunityPricingGroups(catalog?.skus, opportunity);
  const pricingSections = [
    { key: "defaults", title: "Defaults", description: "Standard baseline items for every quote." },
    { key: "features", title: "Features", description: "Services selected on this opportunity." },
    { key: "extras", title: "Extras", description: "Optional add-ons available when needed." },
  ].map((section) => ({
    ...section,
    readySkus: groupedSkus[section.key].filter((sku) =>
      sku.pricingStatus ? sku.pricingStatus === "READY" : sku.active,
    ),
    unconfiguredSkus: groupedSkus[section.key].filter(
      (sku) => sku.pricingStatus === "NEEDS_CONFIGURATION",
    ),
  })).filter((section) => section.readySkus.length || section.unconfiguredSkus.length);
  const updateItem = (skuCode, current, patch) => {
    setPreview(null);
    setItems({ ...items, [skuCode]: { ...current, ...patch } });
  };
  const customPriceFields = (sku, value) => (
    <Grid container spacing={2}>
      <Grid item xs={12} sm={4}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          type="number"
          label={`Custom price for ${sku.name} €`}
          inputProps={{
            min: 0.01,
            step: 0.01,
            "aria-label": `Custom price for ${sku.name} €`,
          }}
          helperText={`${catalog.currency} per ${String(
            sku.billingPeriod || "YEAR",
          ).toLowerCase()}`}
          value={value.customAmount || ""}
          onChange={(event) =>
            updateItem(sku.code, value, { customAmount: event.target.value })
          }
        />
      </Grid>
      <Grid item xs={12} sm={8}>
        <TextField
          fullWidth
          variant="outlined"
          size="small"
          label={`Custom pricing reason for ${sku.name}`}
          inputProps={{
            "aria-label": `Custom pricing reason for ${sku.name}`,
            maxLength: 500,
          }}
          helperText="Required for the immutable quote audit."
          value={value.customPricingReason || ""}
          onChange={(event) =>
            updateItem(sku.code, value, {
              customPricingReason: event.target.value,
            })
          }
        />
      </Grid>
    </Grid>
  );
  const hasInvalidCustomPrice = Object.values(items).some(
    (value) =>
      value.selected &&
      value.customPricing &&
      (Number(value.customAmount || 0) <= 0 ||
        !String(value.customPricingReason || "").trim()),
  );
  return (
    <Dialog
      open
      fullWidth
      maxWidth="md"
      onClose={() => !saving && onClose()}
      aria-labelledby="price-calculator-title"
    >
      <DialogTitle id="price-calculator-title">
        Price {opportunity.company}
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box py={5} textAlign="center">
            <CircularProgress size={26} />
          </Box>
        ) : error && !catalog ? (
          <Box py={2}>
            <Typography color="error">{error}</Typography>
          </Box>
        ) : (
          <>
            <Typography color="textSecondary">
              {catalog.name} · {catalog.code} v{catalog.version} ·{" "}
              {catalog.currency}. The server recalculates every saved quote from
              this published version.
            </Typography>
            {error && (
              <Box mt={2}>
                <Typography color="error" role="alert">
                  {error}
                </Typography>
              </Box>
            )}
            <Box mt={3} display="grid" gridGap={24}>
              {pricingSections.map((section) => (
                <Box key={section.key} component="section" aria-labelledby={`pricing-${section.key}-title`}>
                  <Typography id={`pricing-${section.key}-title`} component="h3" variant="subtitle1">
                    {section.title}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {section.description}
                  </Typography>
                  <Box mt={1} display="grid" gridGap={10}>
              {section.readySkus.map((sku) => {
                  const value = items[sku.code] || {
                    selected: false,
                    quantity: 1,
                    customPricing: false,
                  };
                  return (
                    <Box
                      key={sku.id}
                      display="grid"
                      border="1px solid #e2e6ea"
                      borderRadius={6}
                      px={2}
                      py={1.5}
                      gridGap={12}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gridGap={16}
                      >
                      <FormControlLabel
                        control={
                          <Checkbox
                            color="primary"
                            checked={value.selected}
                            onChange={(event) =>
                              updateItem(sku.code, value, {
                                selected: event.target.checked,
                              })
                            }
                            inputProps={{ "aria-label": sku.name }}
                          />
                        }
                        label={
                          <>
                            <Typography variant="subtitle2">
                              {sku.name}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {sku.code} · {sku.pricingModel} ·{" "}
                              {sku.billingPeriod}
                            </Typography>
                          </>
                        }
                      />
                      {sku.pricingModel !== "FLAT" && (
                        <TextField
                          variant="outlined"
                          size="small"
                          type="number"
                          label={sku.unitLabel || "Quantity"}
                          inputProps={{ min: 0.01, step: 0.01 }}
                          value={value.quantity}
                          onChange={(event) =>
                            updateItem(sku.code, value, {
                              quantity: event.target.value,
                            })
                          }
                          style={{ width: 150 }}
                        />
                      )}
                      </Box>
                      {value.selected && (
                        <>
                          <FormControlLabel
                            control={
                              <Checkbox
                                color="primary"
                                checked={Boolean(value.customPricing)}
                                onChange={(event) =>
                                  updateItem(sku.code, value, {
                                    customPricing: event.target.checked,
                                  })
                                }
                                inputProps={{
                                  "aria-label": `Use custom price for ${sku.name}`,
                                }}
                              />
                            }
                            label="Use a custom price for this quote"
                          />
                          {value.customPricing && customPriceFields(sku, value)}
                        </>
                      )}
                    </Box>
                  );
                })}
                  </Box>
            {section.unconfiguredSkus.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2">
                  Custom pricing
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  These items need an audited custom price before Sales can
                  quote them.
                </Typography>
                <Box mt={1} display="grid" gridGap={8}>
                  {section.unconfiguredSkus.map((sku) => {
                    const value = items[sku.code] || {
                      selected: false,
                      quantity: 1,
                      customPricing: true,
                    };
                    return (
                    <Box
                      key={sku.id}
                      display="grid"
                      border="1px dashed #c7ced4"
                      borderRadius={6}
                      px={2}
                      py={1.25}
                      bgcolor="#f7f8f9"
                      gridGap={12}
                    >
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        gridGap={16}
                      >
                        <FormControlLabel
                          control={
                            <Checkbox
                              color="primary"
                              checked={value.selected}
                              onChange={(event) =>
                                updateItem(sku.code, value, {
                                  selected: event.target.checked,
                                  customPricing: true,
                                })
                              }
                              inputProps={{ "aria-label": sku.name }}
                            />
                          }
                          label={
                            <>
                              <Typography variant="subtitle2">
                                {sku.name}
                              </Typography>
                              <Typography variant="caption" color="textSecondary">
                                {sku.code}
                              </Typography>
                            </>
                          }
                        />
                        <Typography variant="caption" color="textSecondary">
                          Custom price required
                        </Typography>
                      </Box>
                      {value.selected && customPriceFields(sku, value)}
                    </Box>
                    );
                  })}
                </Box>
              </Box>
            )}
                </Box>
              ))}
            </Box>
            <Grid container spacing={2} style={{ marginTop: 12 }}>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Contract years"
                  inputProps={{ min: 1, max: 5 }}
                  value={contractYears}
                  onChange={(event) => {
                    setPreview(null);
                    setContractYears(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Annual increase %"
                  inputProps={{ min: 0, max: 100, step: 0.1 }}
                  value={annualEscalationPercent}
                  onChange={(event) => {
                    setPreview(null);
                    setAnnualEscalationPercent(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Subscription discount % (max 20)"
                  inputProps={{ min: 0, max: 20, step: 0.1, 'aria-label': 'Subscription discount % (max 20)' }}
                  value={discountPercent}
                  onChange={(event) => {
                    setPreview(null);
                    setDiscountPercent(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Setup discount %"
                  inputProps={{ min: 0, max: 100, step: 0.1, 'aria-label': 'Setup discount %' }}
                  value={setupDiscountPercent}
                  onChange={(event) => {
                    setPreview(null);
                    setSetupDiscountPercent(event.target.value);
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <TextField
                  fullWidth
                  variant="outlined"
                  type="number"
                  label="Fixed subscription discount €"
                  inputProps={{ min: 0, step: 0.01 }}
                  value={fixedDiscount}
                  onChange={(event) => {
                    setPreview(null);
                    setFixedDiscount(event.target.value);
                  }}
                />
              </Grid>
            </Grid>
            <Box mt={2}>
              <TextField
                fullWidth
                variant="outlined"
                multiline
                minRows={3}
                label="Special commercial terms"
                helperText="Payment terms, permanence, staged prices, or negotiated exceptions."
                value={specialTerms}
                onChange={(event) => {
                  setPreview(null);
                  setSpecialTerms(event.target.value);
                }}
              />
            </Box>
            {preview?.calculation && (
              <Box
                mt={3}
                p={2}
                bgcolor="#f4f8f5"
                border="1px solid #d7e7dc"
                borderRadius={6}
              >
                <Grid container spacing={2}>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption">Annual recurring</Typography>
                    <Typography variant="h6">
                      {money(
                        preview.calculation.annualRecurringMinor,
                        catalog.currency,
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption">One-time</Typography>
                    <Typography variant="h6">
                      {money(
                        preview.calculation.oneTimeMinor,
                        catalog.currency,
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption">Discount</Typography>
                    <Typography variant="h6">
                      −
                      {money(
                        preview.calculation.discountMinor,
                        catalog.currency,
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={6} sm={3}>
                    <Typography variant="caption">Year 1 total</Typography>
                    <Typography variant="h6">
                      {money(
                        preview.calculation.totalFirstYearMinor,
                        catalog.currency,
                      )}
                    </Typography>
                  </Grid>
                </Grid>
                <Box mt={1} display="flex" gridGap={16} flexWrap="wrap">
                  {(preview.calculation.yearSchedule || []).map((year) => (
                    <Typography variant="body2" key={year.year}>
                      Year {year.year}:{" "}
                      <strong>
                        {money(year.totalMinor, catalog.currency)}
                      </strong>
                    </Typography>
                  ))}
                </Box>
                {(preview.calculation.lines || []).map((line) => (
                  <Box mt={1} key={line.skuCode}>
                    <Typography variant="body2">
                      <strong>{line.name}</strong>: {money(line.amountMinor, catalog.currency)}
                    </Typography>
                    {line.pricingSource === "CUSTOM" && (
                      <Typography variant="caption" color="textSecondary">
                        Custom price
                        {line.catalogAmountMinor !== null
                          ? ` · Catalogue ${money(line.catalogAmountMinor, catalog.currency)}`
                          : " · No approved catalogue price"}
                        {` · ${line.customPricingReason}`}
                      </Typography>
                    )}
                  </Box>
                ))}
              </Box>
            )}
            {quotes.length > 0 && (
              <Box mt={3}>
                <Typography variant="subtitle2">
                  Previous final quotes
                </Typography>
                {quotes.map((quote) => (
                  <Typography
                    variant="body2"
                    color="textSecondary"
                    key={quote.id}
                  >
                    {quote.reference} ·{" "}
                    {money(
                      quote.calculation?.annualRecurringMinor,
                      quote.currency,
                    )}{" "}
                    ARR
                  </Typography>
                ))}
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          onClick={() => request(false)}
          disabled={
            saving ||
            !catalog ||
            !Object.values(items).some((value) => value.selected) ||
            hasInvalidCustomPrice
          }
        >
          Calculate quote
        </Button>
        <Button
          color="primary"
          variant="contained"
          onClick={() => request(true)}
          disabled={saving || !preview?.calculation}
        >
          Save final quote
        </Button>
      </DialogActions>
    </Dialog>
  );
}
